'use client';

import { useClientTranslation } from '@lib/i18n/client';
import {
  DashboardStageOverview,
  type DashboardStageOverviewProps,
} from '@tuic/web-compositions/my-trips/DashboardStageOverview';

import { apiButtonToButtonProps } from '../../elements';

import type PrismAPI from '@modules/prism-client/types/prism-types';
import type { ComponentBlock } from '@modules/prism-client/types/types';
import type { Locale } from '@tuic/i18n';
import type { ButtonProps } from '@tuic/web-compositions/core/Button';

export type StageOverviewBlockData = Omit<
  DashboardStageOverviewProps,
  | 'buttonTripPlan'
  | 'buttonItinerary'
  | 'buttonTripDetails'
  | 'buttonHighlight'
  | 'buttonDailyProgram'
> & {
  locale: Locale;
  buttonTripPlan: PrismAPI.StageOverviewBlock['buttonTripPlan'];
  buttonItinerary: PrismAPI.StageOverviewBlock['buttonItinerary'];
  buttonTripDetails: PrismAPI.StageOverviewBlock['buttonTripDetails'];
  buttonHighlight: PrismAPI.StageOverviewBlock['buttonEvents'];
  buttonDailyProgram: PrismAPI.StageOverviewBlock['buttonDailyProgram'];
};

/**
 * Do this only on the client side because of Radix UI Slot issues when rendering buttons on the server
 * @param data
 * @returns
 */
const prepareButton = (
  data: PrismAPI.ButtonElement | undefined,
): ButtonProps | undefined => {
  if (!data) {
    return undefined;
  }

  return apiButtonToButtonProps({ data });
};

export const StageOverviewBlock: ComponentBlock<StageOverviewBlockData> = ({
  data,
  fallback,
}) => {
  const { t } = useClientTranslation();

  if (fallback) {
    return <DashboardStageOverview fallback />;
  }

  if (!data) {
    return null;
  }

  return (
    <DashboardStageOverview
      cabinNumberText={t('Ihre Kabine:')}
      bookingCodeText={t('Ihre Vorgangsnummer:')}
      {...data}
      buttonTripPlan={prepareButton(data.buttonTripPlan)}
      buttonItinerary={prepareButton(data.buttonItinerary)}
      buttonTripDetails={prepareButton(data.buttonTripDetails)}
      buttonHighlight={prepareButton(data.buttonHighlight)}
      buttonDailyProgram={prepareButton(data.buttonDailyProgram)}
      countdown={
        data.countdown
          ? {
              ...data.countdown,
              textHours: t('Stunden'),
              textMinutes: t('Minuten'),
              textDays: t('Tage'),
            }
          : undefined
      }
    />
  );
};
